/* r11 item 2 (AJ 2026-10-10: "with the full view when they click on the image is there a way to make it not blow up
   their screen and make them move around like to actually just hover and zoom or maybe a different display for the up
   close view?").

   1. Click zoom (mouse screens; AJ 2026-10-12 16:19 replaced the hover zoom, which fired whenever the mouse crossed
      the photo): a click on the product photo shows it at 2x inside its own frame, following the cursor; a second
      click, or the pointer leaving, zooms back out. The corner badge (an expand icon on mouse screens) opens the viewer. Transform only: nothing around it moves. A larger file comes from the
      photo's own srcset (its sizes is raised on the first hover); the browser swaps it in once loaded.
   2. The corner badge, a tap, or Enter on the photo: the photo viewer (snippets/cq-photo-viewer.liquid), a native modal dialog on the side cart's scrim with
      the photo fitted to the window, arrows, thumbnails, Escape and the close button; focus returns to the photo, and the
      page then shows the photo last viewed. Inside it: click to zoom (mouse, follows the pointer, click again to fit);
      on touch, swipe between photos, pinch or double tap to zoom and drag to look around.
   Only the photos the chosen option shows on the page are in the viewer, in the page's order (round 7 rule); videos stay
   in the page gallery. The viewer's slides and thumbnails are made from the gallery's own photos on the first open
   (round 3 fix A1-05), not printed by Liquid for every photo. The page is held with the shared scroll lock (assets/cq-scroll-lock.js). No libraries. */
(() => {
  if (customElements.get('cq-photo-viewer')) return;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mouseScreen = window.matchMedia('(hover: hover) and (pointer: fine)');
  const MAX_ZOOM = 4;
  const CLICK_ZOOM = 2;
  const TAP_ZOOM = 2.5;
  const FRAME = '.product-media-main [data-cq-viewer-frame].product-modal-opener-image';

  // Only the bounded hero framing emitted by Liquid is carried into the viewer.
  // Keep it separate from interactive zoom; copying a whole style attribute could
  // import an in-progress zoom or unrelated presentation into the new image.
  const framingOf = (img) => {
    const scale = Number(img.style.getPropertyValue('scale'));
    const translate = img.style.getPropertyValue('translate').trim().match(/^0(?:px)?\s+(-?(?:\d+\.?\d*|\.\d+))%$/);
    const y = translate ? Number(translate[1]) : NaN;
    return Number.isFinite(scale) && scale >= 1 && scale <= 2 && Number.isFinite(y) && y >= -10 && y <= 10
      ? { scale, y }
      : null;
  };

  // ---------- 1. Click zoom inside the page's photo frame ----------
  const clickZoom = (gallery, signal) => {
    gallery.querySelectorAll(FRAME).forEach((frame) => {
      // v6 gallery templates: run again for frames materialized after start up; the ones already wired are skipped.
      if (frame.cqZoom) return;
      const box = frame.querySelector('.aspect-ratio');
      const img = box?.querySelector('img');
      if (!box || !img) return;
      const framing = framingOf(img);
      if (framing) img.style.transformOrigin = '50% 50%';
      let pending = 0;
      let last = null;
      const place = () => {
        pending = 0;
        if (!last) return;
        const rect = box.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.min(100, Math.max(0, ((last.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((last.clientY - rect.top) / rect.height) * 100));
        if (framing) {
          // The independent base scale stays centred. Compensate the inner zoom's
          // translation so the already-framed point under the cursor stays there.
          const tx = (CLICK_ZOOM - 1) * (50 - x) * rect.width / (100 * framing.scale);
          const ty = (CLICK_ZOOM - 1) * (50 - y + framing.y) * rect.height / (100 * framing.scale);
          img.style.transform = `translate(${tx.toFixed(3)}px, ${ty.toFixed(3)}px) scale(${CLICK_ZOOM})`;
        } else {
          box.style.setProperty('--cq-zoom-x', `${x.toFixed(2)}%`);
          box.style.setProperty('--cq-zoom-y', `${y.toFixed(2)}%`);
        }
      };
      const sharpen = () => {
        if ('cqSharp' in img.dataset) return;
        const rect = box.getBoundingClientRect();
        const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
        const shown = Math.min(rect.width, rect.height * ratio);
        if (!shown) return;
        img.dataset.cqSharp = '';
        img.sizes = `${Math.ceil(shown * CLICK_ZOOM * (framing?.scale || 1))}px`;
      };
      const zoomIn = (event) => {
        sharpen();
        last = event;
        place();
        frame.classList.add('is-zooming');
      };
      const zoomOut = () => {
        cancelAnimationFrame(pending);
        pending = 0;
        last = null;
        frame.classList.remove('is-zooming');
        if (framing) img.style.removeProperty('transform');
        box.style.removeProperty('--cq-zoom-x');
        box.style.removeProperty('--cq-zoom-y');
      };
      frame.cqZoom = { toggle: (event) => (frame.classList.contains('is-zooming') ? zoomOut() : zoomIn(event)), out: zoomOut };
      frame.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !frame.classList.contains('is-zooming')) return;
        last = event;
        if (!pending) pending = requestAnimationFrame(place);
      }, { signal });
      frame.addEventListener('pointerleave', zoomOut, { signal });
    });
  };

  // ---------- 2. The viewer ----------
  customElements.define('cq-photo-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.dialog = this.querySelector('dialog');
      this.gallery = document.getElementById(this.dataset.gallery);
      if (!this.dialog || !this.gallery) return;
      this.cqEvents = new AbortController();
      const options = { signal: this.cqEvents.signal };
      this.track = this.querySelector('[data-cq-lightbox-track]');
      this.thumbRow = this.querySelector('[data-cq-lightbox-thumbs]');
      // A1-05 (round 3 fix): the slides and thumbnails are built from the page gallery on the first open (build()), so
      // the page no longer carries a second and third copy of every photo's markup.
      this.slides = new Map();
      this.thumbs = new Map();
      this.counter = this.querySelector('[data-cq-lightbox-count]');
      this.status = this.querySelector('[data-cq-lightbox-status]');
      this.closeButton = this.querySelector('[data-cq-lightbox-close]');
      this.order = [];
      this.index = 0;
      this.zoom = { s: 1, x: 0, y: 0 };

      clickZoom(this.gallery, this.cqEvents.signal);

      // The whole photo is its "Zoom image N" button. With a mouse, a click zooms it in place and a second click zooms
      // out; the corner badge opens the viewer. A tap, or Enter or Space (click with detail 0), opens the viewer.
      this.gallery.addEventListener('pointerdown', (event) => { this.lastPointer = event.pointerType; }, { ...options, passive: true });
      this.gallery.addEventListener('click', (event) => {
        const badge = event.target.closest(`${FRAME} .product-media-icon-enlarge`);
        const toggle = badge
          ? badge.closest(FRAME)?.querySelector('.product-media-toggle')
          : event.target.closest(`${FRAME} .product-media-toggle`);
        if (!toggle || !this.gallery.contains(toggle)) return;
        event.preventDefault();
        const frame = toggle.closest(FRAME);
        // v6 gallery templates: a photo brought into the page after start up (assets/media-gallery.js materialize(), on
        // an option change) gets its click zoom on its first click, like the photos live at start up.
        if (frame && !frame.cqZoom) clickZoom(this.gallery, this.cqEvents.signal);
        const mouse = this.lastPointer === 'mouse';
        this.lastPointer = null;
        if (!badge && event.detail > 0 && mouse && mouseScreen.matches && frame?.cqZoom) {
          frame.cqZoom.toggle(event);
          return;
        }
        frame?.cqZoom?.out();
        this.open(toggle.dataset.mediaId, toggle);
      }, options);

      this.closeButton.addEventListener('click', () => this.close(), options);
      this.querySelectorAll('[data-cq-lightbox-step]').forEach((button) => {
        button.addEventListener('click', () => this.go(this.index + Number(button.dataset.cqLightboxStep) * this.dir()), options);
      });
      this.thumbRow.addEventListener('click', (event) => {
        const thumb = event.target.closest('.cq-lightbox-thumb');
        if (thumb) this.go(this.order.indexOf(thumb.dataset.mediaId));
      }, options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      // Every close path ends here: unlock the page, reset, show the last photo on the page, return focus to it.
      this.dialog.addEventListener('close', () => this.cleanup(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.onKey(event), options);
      // A slide's photo ground arrives once its photo has loaded (assets/cq-media-ground.js).
      this.dialog.addEventListener('cq:ground', (event) => { if (event.target === this.current()) this.ground(); }, options);
      this.track.addEventListener('scroll', () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.syncFromScroll(); });
      }, { ...options, passive: true });
      window.addEventListener('resize', () => {
        if (!this.dialog.open) return;
        this.resetZoom(false);
        this.snap(false);
      }, options);
      window.addEventListener('pageshow', () => { if (!this.dialog.open) window.cqScrollLock?.unlock(this); }, options);

      // Zoom inside the viewer.
      this.track.addEventListener('click', (event) => this.onClick(event), options);
      this.track.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || this.zoom.s <= 1) return;
        this.lastMove = event;
        if (!this.moveFrame) this.moveFrame = requestAnimationFrame(() => { this.moveFrame = 0; if (this.lastMove && this.zoom.s > 1) this.follow(this.lastMove, this.zoom.s, false); });
      }, options);
      this.track.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && this.zoom.s > 1) this.resetZoom(true);
      }, options);
      this.track.addEventListener('touchstart', (event) => this.onTouchStart(event), { ...options, passive: false });
      this.track.addEventListener('touchmove', (event) => this.onTouchMove(event), { ...options, passive: false });
      this.track.addEventListener('touchend', (event) => this.onTouchEnd(event), options);
      this.track.addEventListener('touchcancel', (event) => this.onTouchEnd(event), options);
      // iOS Safari zooms the whole page on a pinch unless its gesture event is cancelled.
      this.dialog.addEventListener('gesturestart', (event) => event.preventDefault(), options);
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      window.cqScrollLock?.unlock(this);
      this.cqEvents?.abort();
      this.cqEvents = null;
    }

    dir() {
      return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    }

    // A1-05: one slide and one thumbnail per gallery photo, made once, from the photo the page already has: the same
    // file set (srcset), the same alt text, sized for the viewer. Videos and 3D models have no image opener and stay
    // in the page gallery. Hidden slides are lazy, so nothing downloads until a photo is shown (load()).
    build() {
      // v6 gallery templates (2026-10-29): the photos the chosen option hides ship inside <template> elements in the
      // page gallery (assets/media-gallery.js materialize()) and stay there; the viewer shows the option's photos, which
      // are live, and build() is incremental, so a later option change adds its photos on the next open. Nothing is
      // brought into the page here, which keeps the page's saving after the viewer opens.
      this.gallery.querySelectorAll('.product-media-main').forEach((main) => {
        const id = String(main.dataset.mediaId);
        const source = main.querySelector('[data-cq-viewer-frame].product-modal-opener-image .aspect-ratio img');
        if (!source || this.slides.has(id)) return;
        const src = source.getAttribute('src') || '';
        const slide = document.createElement('div');
        slide.className = 'cq-lightbox-slide';
        slide.dataset.mediaId = id;
        slide.hidden = true;
        const img = document.createElement('img');
        const framing = framingOf(source);
        if (framing) img.cqFraming = framing;
        img.loading = 'lazy';
        img.className = 'cq-lightbox-img';
        img.draggable = false;
        img.alt = source.getAttribute('alt') || '';
        ['width', 'height'].forEach((name) => { if (source.hasAttribute(name)) img.setAttribute(name, source.getAttribute(name)); });
        // The photo's shape sizes it to the stage (assets/cq-photo-viewer.css), whatever pixel size its file has.
        const ratio = Number(source.getAttribute('width')) / Number(source.getAttribute('height'));
        if (ratio > 0 && Number.isFinite(ratio)) img.style.setProperty('--cq-photo-ratio', ratio.toFixed(4));
        img.sizes = this.dataset.slideSizes || '100vw';
        if (source.getAttribute('srcset')) img.srcset = source.getAttribute('srcset');
        img.src = src;
        slide.append(img);
        this.track.append(slide);
        this.slides.set(id, slide);

        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'cq-lightbox-thumb';
        thumb.dataset.mediaId = id;
        thumb.setAttribute('aria-current', 'false');
        thumb.hidden = true;
        const small = document.createElement('img');
        small.loading = 'lazy';
        small.width = 64;
        small.height = 64;
        small.alt = '';
        small.draggable = false;
        if (framing) {
          small.style.scale = String(framing.scale);
          small.style.translate = `0 ${framing.y}%`;
        }
        try {
          const url = new URL(src, window.location.href);
          url.searchParams.set('width', '160');
          small.src = url.href;
        } catch (error) {
          small.src = src;
        }
        thumb.append(small);
        this.thumbRow.append(thumb);
        this.thumbs.set(id, thumb);
      });
    }

    // The photos the page shows for the chosen option, in the order the page shows them (an option change re-orders the
    // gallery's elements, so the live document order is read, not a list kept from page load).
    shownIds() {
      this.build();
      return [...this.gallery.querySelectorAll('.product-media-main:not(.cq-photo-off)')]
        .map((element) => String(element.dataset.mediaId))
        .filter((id) => this.slides.has(id));
    }

    open(mediaId, opener) {
      if (this.dialog.open) return;
      const order = this.shownIds();
      if (!order.length) return;
      this.order = order;
      const wanted = new Set(order);
      this.slides.forEach((slide, id) => {
        slide.hidden = !wanted.has(id);
        slide.classList.remove('is-current');
      });
      this.thumbs.forEach((thumb, id) => { thumb.hidden = !wanted.has(id); });
      const thumbLabel = this.dataset.thumbLabel || '';
      order.forEach((id, index) => {
        this.track.append(this.slides.get(id));
        const thumb = this.thumbs.get(id);
        if (!thumb) return;
        this.thumbRow.append(thumb);
        thumb.setAttribute('aria-label', thumbLabel.replace('__N__', index + 1));
      });
      this.dialog.classList.toggle('is-single', order.length < 2);
      this.dialog.classList.toggle('cq-media-on-white', this.gallery.classList.contains('cq-media-on-white'));
      this.opener = opener;
      this.gallery.querySelectorAll('.is-zooming').forEach((frame) => frame.cqZoom?.out());
      const index = Math.max(0, order.indexOf(String(mediaId)));
      this.load(index);
      window.cqScrollLock?.lock(this, { root: this.dialog, isOpen: () => this.dialog.open });
      this.dialog.showModal();
      this.zoom = { s: 1, x: 0, y: 0 };
      this.index = -1;
      this.setIndex(index, false);
      this.snap(false);
      this.closeButton.focus({ preventScroll: true });
      this.announce();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    cleanup() {
      window.cqScrollLock?.unlock(this);
      this.resetZoom(false);
      this.pinch = null;
      this.pan = null;
      clearTimeout(this.announceTimer);
      this.status.textContent = '';
      const id = this.order[this.index];
      if (id && typeof this.gallery.setActiveMedia === 'function') this.gallery.setActiveMedia(id, false);
      const main = [...this.gallery.querySelectorAll('.product-media-main')].find((element) => element.dataset.mediaId === id);
      const target = main?.querySelector('.product-media-toggle') || this.opener;
      // On phones the gallery is a carousel whose hidden slides are inert until it has moved to the photo; try again then.
      const focus = () => {
        const active = document.activeElement;
        // A click on the corner badge (not focusable) leaves focus on <main>; that counts as lost too.
        const lost = !active || active === document.body || active === this.opener || active.matches('main, [tabindex="-1"]');
        if (target?.isConnected && active !== target && lost && !this.dialog.open) target.focus({ preventScroll: true });
      };
      focus();
      requestAnimationFrame(focus);
      setTimeout(focus, 400);
    }

    // Start loading the photo shown and its neighbours.
    load(index) {
      [index - 1, index, index + 1].forEach((i) => {
        const img = this.slides.get(this.order[i])?.querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';
      });
    }

    go(index) {
      const count = this.order.length;
      if (count < 2 || index < 0) return;
      const next = ((index % count) + count) % count;
      this.load(next);
      this.setIndex(next, true);
      this.snap(true);
    }

    snap(smooth) {
      const left = this.index * this.track.clientWidth * this.dir();
      this.track.scrollTo({ left, behavior: smooth && !reduced() ? 'smooth' : 'instant' });
    }

    syncFromScroll() {
      if (!this.dialog.open || this.zoom.s > 1 || this.pinch) return;
      const width = this.track.clientWidth;
      if (!width) return;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (index === this.index || index < 0 || index >= this.order.length) return;
      this.load(index);
      this.setIndex(index, true);
    }

    setIndex(index, announce) {
      if (index !== this.index) this.resetZoom(false);
      this.index = index;
      const count = this.order.length;
      this.order.forEach((id, i) => {
        const slide = this.slides.get(id);
        slide.classList.toggle('is-current', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
        const thumb = this.thumbs.get(id);
        if (thumb) thumb.setAttribute('aria-current', String(i === index));
      });
      this.counter.textContent = `${index + 1} / ${count}`;
      this.apply(false);
      this.ground();
      this.revealThumb(this.thumbs.get(this.order[index]));
      if (announce) {
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => this.announce(), 350);
      }
    }

    // The whole viewer takes the current photo's matched ground; without one it keeps the studio grey (CSS default).
    ground() {
      const value = this.current()?.style.getPropertyValue('--cq-tile').trim();
      if (value) this.dialog.style.setProperty('--cq-lightbox-ground', value);
      else this.dialog.style.removeProperty('--cq-lightbox-ground');
    }

    announce() {
      if (!this.dialog.open || this.order.length < 2) return;
      this.status.textContent = (this.dataset.countLabel || '').replace('__N__', this.index + 1).replace('__C__', this.order.length);
    }

    // Keep the current thumbnail in view inside its own row (never scrolls anything else).
    revealThumb(thumb) {
      const row = this.thumbRow;
      if (!thumb || row.scrollWidth <= row.clientWidth) return;
      const box = row.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      const behavior = reduced() ? 'instant' : 'smooth';
      if (rect.left < box.left + 16) row.scrollBy({ left: rect.left - box.left - 16, behavior });
      else if (rect.right > box.right - 16) row.scrollBy({ left: rect.right - box.right + 16, behavior });
    }

    onKey(event) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1) * this.dir());
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.go(event.key === 'Home' ? 0 : this.order.length - 1);
      } else if (event.key === 'Tab') {
        const controls = [...this.dialog.querySelectorAll('button:not(:disabled)')]
          .filter((element) => !element.hidden && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    // ---------- zoom ----------
    current() {
      return this.slides.get(this.order[this.index]) || null;
    }

    geometry() {
      const slide = this.current();
      const img = slide?.querySelector('img');
      if (!slide || !img) return null;
      // offset* ignore transforms. For a framed hero, interactive zoom operates
      // on its larger base box, including the fixed focal translation.
      const scale = img.cqFraming?.scale || 1;
      const baseX = (1 - scale) * img.offsetWidth / 2;
      const baseY = (1 - scale) * img.offsetHeight / 2 + (img.cqFraming?.y || 0) * img.offsetHeight / 100;
      return { img, rect: slide.getBoundingClientRect(), ox: img.offsetLeft + baseX, oy: img.offsetTop + baseY,
        w: img.offsetWidth * scale, h: img.offsetHeight * scale, sw: slide.clientWidth, sh: slide.clientHeight, baseX, baseY, baseScale: scale };
    }

    // Keep the zoomed photo covering the slide (no empty edge); centre it on an axis where it is smaller than the slide.
    clamp(s, tx, ty, g) {
      const cw = g.w * s;
      const ch = g.h * s;
      const x = cw <= g.sw ? (g.sw - cw) / 2 - g.ox : Math.min(-g.ox, Math.max(g.sw - g.ox - cw, tx));
      const y = ch <= g.sh ? (g.sh - ch) / 2 - g.oy : Math.min(-g.oy, Math.max(g.sh - g.oy - ch, ty));
      return [x, y];
    }

    apply(easing) {
      const g = this.geometry();
      if (!g) return;
      const { s, x, y } = this.zoom;
      // A zoomed photo asks its srcset for a larger file once; the browser swaps it in when it has loaded.
      if (s > 1 && !('cqSharp' in g.img.dataset) && g.w) {
        g.img.dataset.cqSharp = '';
        g.img.sizes = `${Math.ceil(g.w * 2.5)}px`;
      }
      const ease = Boolean(easing) && !reduced();
      g.img.classList.toggle('is-easing', ease);
      g.img.style.transform = s > 1 || g.img.cqFraming
        ? `translate(${(g.baseX + x).toFixed(1)}px, ${(g.baseY + y).toFixed(1)}px) scale(${(g.baseScale * s).toFixed(3)})`
        : '';
      this.dialog.classList.toggle('is-zoomed', s > 1);
      clearTimeout(this.easeTimer);
      if (ease) this.easeTimer = setTimeout(() => g.img.classList.remove('is-easing'), 300);
    }

    // Zoom to s around a point of the slide (px from its top left corner): the part of the photo under it stays there.
    zoomAt(s, fx, fy, easing) {
      const g = this.geometry();
      if (!g) return;
      const scale = Math.min(MAX_ZOOM, Math.max(1, s));
      const old = this.zoom;
      const qx = (fx - g.ox - old.x) / old.s;
      const qy = (fy - g.oy - old.y) / old.s;
      const [x, y] = this.clamp(scale, fx - g.ox - qx * scale, fy - g.oy - qy * scale, g);
      this.zoom = scale > 1 ? { s: scale, x, y } : { s: 1, x: 0, y: 0 };
      this.apply(easing);
    }

    // Mouse: the photo follows the pointer, so moving across it shows every part of it.
    follow(event, s, easing) {
      const g = this.geometry();
      if (!g) return;
      const fx = event.clientX - g.rect.left;
      const fy = event.clientY - g.rect.top;
      const qx = Math.min(g.w, Math.max(0, fx - g.ox));
      const qy = Math.min(g.h, Math.max(0, fy - g.oy));
      const [x, y] = this.clamp(s, fx - g.ox - qx * s, fy - g.oy - qy * s, g);
      this.zoom = { s, x, y };
      this.apply(easing);
    }

    resetZoom(easing) {
      const g = this.geometry();
      this.zoom = { s: 1, x: 0, y: 0 };
      if (g && g.img.style.transform) this.apply(easing);
      else this.dialog.classList.remove('is-zoomed');
    }

    onClick(event) {
      if (!mouseScreen.matches || (event.pointerType && event.pointerType !== 'mouse')) return;
      const img = event.target.closest('.cq-lightbox-img');
      if (!img || img !== this.current()?.querySelector('img')) return;
      if (this.zoom.s > 1) this.resetZoom(true);
      else this.follow(event, CLICK_ZOOM, true);
    }

    onTouchStart(event) {
      const g = this.geometry();
      if (!g) return;
      if (event.touches.length === 2) {
        event.preventDefault();
        const [a, b] = event.touches;
        this.pinch = {
          d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1,
          s: this.zoom.s,
          x: this.zoom.x,
          y: this.zoom.y,
          mx: (a.clientX + b.clientX) / 2 - g.rect.left,
          my: (a.clientY + b.clientY) / 2 - g.rect.top,
        };
        this.pan = null;
        this.lastTap = null;
        return;
      }
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const now = performance.now();
      // Double tap: zoom in at that point, or back to the fitted photo.
      if (this.lastTap && now - this.lastTap.time < 320 && Math.hypot(touch.clientX - this.lastTap.x, touch.clientY - this.lastTap.y) < 30) {
        event.preventDefault();
        this.lastTap = null;
        if (this.zoom.s > 1) this.resetZoom(true);
        else this.zoomAt(TAP_ZOOM, touch.clientX - g.rect.left, touch.clientY - g.rect.top, true);
        return;
      }
      this.lastTap = { time: now, x: touch.clientX, y: touch.clientY };
      if (this.zoom.s > 1) this.pan = { x: touch.clientX, y: touch.clientY, tx: this.zoom.x, ty: this.zoom.y };
    }

    onTouchMove(event) {
      if (this.pinch && event.touches.length >= 2) {
        event.preventDefault();
        const g = this.geometry();
        if (!g) return;
        const [a, b] = event.touches;
        const scale = Math.min(MAX_ZOOM, Math.max(1, (this.pinch.s * Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)) / this.pinch.d));
        const mx = (a.clientX + b.clientX) / 2 - g.rect.left;
        const my = (a.clientY + b.clientY) / 2 - g.rect.top;
        // The part of the photo that was under the fingers stays under them while they move apart, together or along.
        const qx = (this.pinch.mx - g.ox - this.pinch.x) / this.pinch.s;
        const qy = (this.pinch.my - g.oy - this.pinch.y) / this.pinch.s;
        const [x, y] = this.clamp(scale, mx - g.ox - qx * scale, my - g.oy - qy * scale, g);
        this.zoom = { s: scale, x, y };
        this.apply(false);
        return;
      }
      if (this.pan && this.zoom.s > 1 && event.touches.length === 1) {
        event.preventDefault();
        const g = this.geometry();
        if (!g) return;
        const touch = event.touches[0];
        if (Math.hypot(touch.clientX - this.pan.x, touch.clientY - this.pan.y) > 10) this.lastTap = null;
        const [x, y] = this.clamp(this.zoom.s, this.pan.tx + touch.clientX - this.pan.x, this.pan.ty + touch.clientY - this.pan.y, g);
        this.zoom = { s: this.zoom.s, x, y };
        this.apply(false);
      }
    }

    onTouchEnd(event) {
      if (this.pinch && event.touches.length < 2) {
        this.pinch = null;
        if (this.zoom.s < 1.08) this.resetZoom(true);
        if (event.touches.length === 1 && this.zoom.s > 1) {
          const touch = event.touches[0];
          this.pan = { x: touch.clientX, y: touch.clientY, tx: this.zoom.x, ty: this.zoom.y };
        }
      }
      if (!event.touches.length) this.pan = null;
    }
  });
})();
